import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, input, output, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'lib-range-input',
  imports: [FormsModule, CommonModule],
  templateUrl: './range-input.html',
  styleUrl: './range-input.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RangeInput),
      multi: true,
    },
  ],
})
export class RangeInput implements ControlValueAccessor {
  public min = input.required<number>();
  public max = input.required<number>();
  public label = input.required<string>();
  public inputId = input.required<string>();
  public step = input<number>(1);
  // A slider that runs through zero measures one thing in two directions. Naming both directions turns its
  // ends into the scale, so the value can read as a distance in one of them instead of a negative number.
  public negativeLabel = input<string | null>(null);
  public positiveLabel = input<string | null>(null);

  // Emits on every movement while dragging; the form control itself only updates on release (change event).
  public liveValueChange = output<number>();

  protected value = signal<number | null | undefined>(undefined);
  protected disabled = signal<boolean>(false);
  protected displayValue = signal<string>('');

  protected hasDirectionLabels = computed<boolean>(() => !!this.negativeLabel() && !!this.positiveLabel());

  // The direction a value runs in is named above the slider and shown by the side the track fills from, so
  // the readout states the distance itself rather than repeating either.
  protected displayText = computed<string>(() => {
    const displayValue = this.displayValue();

    if (displayValue === '') {
      return displayValue;
    }

    if (!this.hasDirectionLabels()) {
      return displayValue;
    }

    return `${Math.abs(Number(displayValue))}`;
  });

  /**
   * Computed percentage for styling the progress of the range input
   */
  protected progressPercentage = computed(() => {
    const val = this.value() ?? this.min();
    const min = this.min();
    const max = this.max();
    return ((val - min) / (max - min)) * 100;
  });

  // The filled part of the track starts where zero sits, so a slider running below zero grows out of its
  // middle in the direction it is dragged instead of always out of its left end.
  protected fillStartPercentage = computed(() => Math.min(this.zeroPercentage(), this.progressPercentage()));
  protected fillEndPercentage = computed(() => Math.max(this.zeroPercentage(), this.progressPercentage()));

  private zeroPercentage = computed(() => {
    const min = this.min();

    if (min >= 0) {
      return 0;
    }

    return (-min / (this.max() - min)) * 100;
  });

  private onChange: (value: number | null | undefined) => void = () => {
    /* intentionally empty */
  };
  private onTouched: () => void = () => {
    /* intentionally empty */
  };

  writeValue(obj: number | null | undefined): void {
    if (obj !== this.value()) {
      this.value.set(obj);
      this.displayValue.set(obj?.toString() ?? '');
    }
  }

  registerOnChange(fn: (value: number | null | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.displayValue.set(target.value);
    this.liveValueChange.emit(target.valueAsNumber);
  }

  protected onValueChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.valueAsNumber;

    this.value.set(newValue);

    setTimeout(() => this.onChange(newValue));
  }

  protected onBlur(): void {
    this.onTouched();
  }
}
